import React from 'react';
import styled from 'styled-components';

const SectionWrapper = styled.section`
  padding: 80px 5% 120px;
  background: #fbfdff;
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  text-align: center;
`;

const Title = styled.h2`
  font-family: 'Open Sans', sans-serif;
  font-size: 38px;
  font-weight: 800;
  color: #1a202c; /* Better contrast */
  margin-bottom: 12px;
`;

const Subtitle = styled.p`
  font-family: 'Open Sans', sans-serif;
  font-size: 16px;
  color: #4a5568;
  margin-bottom: 5px;
  font-weight: 500;
`;

const Grid = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 30px;
  margin-top: 60px;

  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

const Card = styled.div`
  background: #ffffff;
  border-radius: 8px; /* Tighter radius */
  padding: 40px 30px;
  width: 330px; /* Slightly wider */
  box-shadow: 0 4px 20px rgba(0,0,0,0.06); /* Stronger baseline shadow */
  position: relative;
  transition: transform 0.3s, box-shadow 0.3s;
  ${({ popular }) => popular && `
    transform: scale(1.06);
    box-shadow: 0 12px 35px rgba(0,0,0,0.12);
    z-index: 2;
    border: 1px solid #e1eff6;
  `}

  &:hover {
    ${({ popular }) => !popular && `
      transform: translateY(-5px);
      box-shadow: 0 10px 25px rgba(0,0,0,0.1);
    `}
  }

  @media (max-width: 900px) {
    width: 100%;
    max-width: 400px;
    ${({ popular }) => popular && `transform: none;`}
  }
`;

const PlanName = styled.h4`
  color: #fa6582;
  font-family: 'Open Sans', sans-serif;
  font-size: 15px;
  font-weight: 700;
  text-transform: uppercase;
  margin-bottom: ${({ popular }) => popular ? '5px' : '20px'};
  letter-spacing: 1px;
`;

const MostPopularLabel = styled.div`
  font-family: 'Montserrat', sans-serif;
  font-size: 16px;
  font-weight: 800;
  color: #1a202c;
  margin-bottom: 20px;
`;

const Price = styled.div`
  font-family: 'Open Sans', sans-serif;
  color: #25aae3;
  margin-bottom: 30px;
  display: flex;
  justify-content: center;
  align-items: baseline;
`;

const Currency = styled.span`
  font-size: 20px;
  color: #4a5568;
  margin-right: 4px;
`;

const Amount = styled.span`
  font-size: 60px;
  font-weight: 800;
  line-height: 1;
`;

const Period = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: #718096;
  margin-left: 4px;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 40px 0;
`;

const FeatureItem = styled.li`
  font-family: 'Open Sans', sans-serif;
  font-size: 14px;
  color: #4a5568;
  padding: 12px 0;
  font-weight: ${({ bold }) => bold ? '700' : '500'};
  border-bottom: 1px solid #f0f4f8;

  &:last-child {
    border-bottom: none;
  }
`;

const SelectButton = styled.a`
  display: block; /* anchor block styling */
  background: #25aae3;
  color: #fff;
  border: none;
  padding: 16px 0;
  width: 100%;
  border-radius: 4px;
  font-size: 14px;
  font-family: 'Montserrat', sans-serif;
  font-weight: 800;
  text-decoration: none;
  cursor: pointer;
  box-shadow: 0 4px 10px rgba(37,170,227,0.3);
  transition: all 0.3s ease;

  &:hover {
    background: #1c94c9;
    box-shadow: 0 6px 15px rgba(37,170,227,0.4);
    transform: translateY(-2px);
  }
`;

const Pricing = () => {
  return (
    <SectionWrapper id="pricing">
      <Container>
        <Title>Pricing</Title>
        <Subtitle>Select the Gainsta plan that works best for your account goals!</Subtitle>
        <Subtitle>Not sure? Feel free to contact us for advice!</Subtitle>

        <Grid>
          <Card>
            <PlanName>Starter</PlanName>
            <Price>
              <Currency>$</Currency><Amount>19.99</Amount><Period>/mo</Period>
            </Price>
            <FeatureList>
              <FeatureItem>One Account</FeatureItem>
              <FeatureItem bold>Standard Support</FeatureItem>
              <FeatureItem>Instagram Scheduling and Automation</FeatureItem>
              <FeatureItem>Spintax</FeatureItem>
              <FeatureItem style={{ color: '#a0aec0' }}>Watermark Image Editor</FeatureItem>
            </FeatureList>
            <SelectButton href="#">GET STARTED TODAY</SelectButton>
          </Card>

          <Card popular>
            <PlanName>Starter + Pro</PlanName>
            <MostPopularLabel>Most Popular</MostPopularLabel>
            <Price>
              <Currency>$</Currency><Amount>24.99</Amount><Period>/mo</Period>
            </Price>
            <FeatureList>
              <FeatureItem>One Account</FeatureItem>
              <FeatureItem bold>Premium Support</FeatureItem>
              <FeatureItem>Instagram Scheduling and Automation</FeatureItem>
              <FeatureItem>Spintax</FeatureItem>
              <FeatureItem>Watermark Image Editor</FeatureItem>
            </FeatureList>
            <SelectButton href="#">GET STARTED TODAY</SelectButton>
          </Card>

          <Card>
            <PlanName>Professional</PlanName>
            <Price>
              <Currency>$</Currency><Amount>49.99</Amount><Period>/mo</Period>
            </Price>
            <FeatureList>
              <FeatureItem bold>Three Accounts</FeatureItem>
              <FeatureItem bold>Premium Support</FeatureItem>
              <FeatureItem>Instagram Scheduling and Automation</FeatureItem>
              <FeatureItem>Spintax</FeatureItem>
              <FeatureItem>Watermark Image Editor</FeatureItem>
            </FeatureList>
            <SelectButton href="#">GET STARTED TODAY</SelectButton>
          </Card>
        </Grid>
      </Container>
    </SectionWrapper>
  );
};

export default Pricing;
